import React, { Suspense } from "react";
import { Flex, FormControl, FormLabel, Switch, Text } from "@chakra-ui/react";
import CurrencySwitch from "@/shared/components/currency-switch";
import { getCurrencySymbol } from "@/features/types";
import HelpTooltip from "@/shared/components/help-tooltip";
import { useSearchParams } from "react-router-dom";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Trans } from "react-i18next";
import useFinalCopyCurrency from "@/features/my-order/features/order-history/features/final-copy/hooks/useFinalCopyCurrency";

function OrderHistoryFinalCopyFilterBar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isConfirmedOnly = searchParams.get("confirmedOnly") === "true";
  const isKrw = searchParams.get("currency") === "KRW";

  const handleConfirmedOnlyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    if (checked) {
      searchParams.set("confirmedOnly", "true");
    } else {
      searchParams.delete("confirmedOnly");
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleCurrencyChange = (checked: boolean) => {
    if (checked) {
      searchParams.set("currency", "KRW");
    } else {
      searchParams.delete("currency");
    }
    setSearchParams(searchParams, { replace: true });
  };

  const { tSentence, tTitle } = useI18nHelper();

  return (
    <Flex
      gap={"16px"}
      padding={"12px 16px"}
      boxShadow={"0 0.5px 0 0 #D4D4D1"}
      alignItems={"center"}
      marginBottom={"0.5px"}
    >
      <FormControl
        display="flex"
        alignItems="center"
        gap={"8px"}
        width={"fit-content"}
      >
        <FormLabel htmlFor="confirmed-only" margin={"0px"}>
          <Text textStyle={"body-sm-regular"} color={"#444444"}>
            {tSentence("view_confirmed_quantity_only")}
          </Text>
        </FormLabel>
        <Switch
          id="confirmed-only"
          isChecked={isConfirmedOnly}
          onChange={handleConfirmedOnlyChange}
        />
      </FormControl>
      {/* 통화 스위치는 확정본 데이터(라인시트 통화)를 받은 뒤에 정한다 — 그동안 나머지 바는 그대로 보인다 */}
      <Suspense fallback={null}>
        <FinalCopyCurrencyToggle
          isKrw={isKrw}
          onChange={handleCurrencyChange}
        />
      </Suspense>
    </Flex>
  );
}

// 원화 라인시트는 바꿀 통화가 없어 통화 스위치를 감춘다
function FinalCopyCurrencyToggle({
  isKrw,
  onChange,
}: {
  isKrw: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { tTitle } = useI18nHelper();
  const { baseCurrency, isBaseKrw } = useFinalCopyCurrency();

  return (
    <>
      {!isBaseKrw && (
        <>
          <Text fontSize={"16px"} color={"#D4D4D1"}>
            |
          </Text>
          <Flex gap={"8px"} alignItems={"center"}>
            <Flex gap={"2px"} alignItems={"center"}>
              <Text textStyle={"body-sm-regular"} color={"#444444"}>
                {tTitle("currency")}
              </Text>
              <HelpTooltip
                text={
                  <Trans
                    i18nKey={"order_history:currency_tooltip"}
                    components={{
                      br: <br />,
                    }}
                  />
                }
              />
            </Flex>
            <CurrencySwitch
              checked={isKrw}
              onChange={onChange}
              firstOption={getCurrencySymbol(baseCurrency)}
              secondOption={"₩"}
            />
          </Flex>
        </>
      )}
    </>
  );
}

export default OrderHistoryFinalCopyFilterBar;
